import { useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, type FieldPath, type Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import Loading from "@/components/Loading";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { VALUE_FORMAT_LABEL_KEYS, VALUE_FORMATS } from "../../constants";
import { type FarmerIdentificationModalOptions } from "../../types";

interface FormProps {
  initialValues?: Partial<FarmerIdentificationFormValues>;
  isFetching?: boolean;
  modalOptions: FarmerIdentificationModalOptions;
  onSubmit: (values: FarmerIdentificationFormValues) => void;
}

export const FARMER_IDENTIFICATION_FORM_ID = "farmer-identification-form";

const toOptionalNumber = (value: unknown) =>
  value === "" || value === null || value === undefined ? undefined : Number(value);

export const createFarmerIdentificationSchema = ({
  maxVsMinMessage,
  maximumZeroMessage,
  minVsMaxMessage,
  minimumZeroMessage,
  requiredMessage,
}: {
  maxVsMinMessage: string;
  maximumZeroMessage: string;
  minVsMaxMessage: string;
  minimumZeroMessage: string;
  requiredMessage: string;
}) => {
  const lengthSchema = (zeroMessage: string) =>
    z.preprocess(toOptionalNumber, z.number({ error: zeroMessage }).min(1, { error: zeroMessage }));

  return z
    .object({
      active: z.boolean().default(false),
      code: z.preprocess(
        value => value ?? "",
        z.string().trim().min(1, { error: requiredMessage })
      ),
      name: z.preprocess(
        value => value ?? "",
        z.string().trim().min(1, { error: requiredMessage })
      ),
      value_format: z.preprocess(
        value => (value === "" || value === null ? undefined : value),
        z.enum(VALUE_FORMATS, { error: requiredMessage })
      ),
      value_maximum_length: lengthSchema(maximumZeroMessage),
      value_minimum_length: lengthSchema(minimumZeroMessage),
    })
    .superRefine((values, ctx) => {
      if (values.value_minimum_length <= values.value_maximum_length) {
        return;
      }

      // Both messages are raised so each input explains the mismatch on its own.
      ctx.addIssue({ code: "custom", message: minVsMaxMessage, path: ["value_minimum_length"] });
      ctx.addIssue({ code: "custom", message: maxVsMinMessage, path: ["value_maximum_length"] });
    });
};

export type FarmerIdentificationFormValues = z.infer<
  ReturnType<typeof createFarmerIdentificationSchema>
>;

const normalizeInitialValues = (initialValues?: Partial<FarmerIdentificationFormValues>) => ({
  active: initialValues?.active ?? true,
  code: initialValues?.code ?? "",
  name: initialValues?.name ?? "",
  value_format: initialValues?.value_format,
  value_maximum_length: initialValues?.value_maximum_length,
  value_minimum_length: initialValues?.value_minimum_length,
});

export const Form = ({ initialValues, isFetching = false, modalOptions, onSubmit }: FormProps) => {
  const { t } = useTranslation();
  const isViewMode = modalOptions.type === "view";
  const schema = useMemo(
    () =>
      createFarmerIdentificationSchema({
        maxVsMinMessage: t("farmerIdentification.form.maximumLengthValidationErrorText"),
        maximumZeroMessage: t("farmerIdentification.form.maximumLengthValueValidation"),
        minVsMaxMessage: t("farmerIdentification.form.minimumLengthValidationErrorText"),
        minimumZeroMessage: t("farmerIdentification.form.minimumLengthValueValidation"),
        requiredMessage: t("traderDetails.form.mandatoryFieldBlankError"),
      }),
    [t]
  );
  const defaultValues = useMemo(() => normalizeInitialValues(initialValues), [initialValues]);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<FarmerIdentificationFormValues>({
    defaultValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(schema) as Resolver<FarmerIdentificationFormValues>,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, modalOptions.id, modalOptions.type, reset]);

  const getFieldError = (fieldName: FieldPath<FarmerIdentificationFormValues>) =>
    errors[fieldName]?.message ?? "";

  if (isFetching) {
    return <Loading isVisible />;
  }

  return (
    <form
      id={FARMER_IDENTIFICATION_FORM_ID}
      className="space-y-5"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      <Controller
        control={control}
        name="name"
        render={({ field }) => {
          const error = getFieldError("name");

          return (
            <Field data-invalid={Boolean(error)}>
              <FieldLabel htmlFor="input-name">
                {t("configuration.common.name")}
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              </FieldLabel>
              <Input
                {...field}
                id="input-name"
                value={field.value ?? ""}
                disabled={isViewMode}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? "input-name-error" : undefined}
              />
              <FieldError id="input-name-error">{error}</FieldError>
            </Field>
          );
        }}
      />

      <Controller
        control={control}
        name="code"
        render={({ field }) => {
          const error = getFieldError("code");

          return (
            <Field data-invalid={Boolean(error)}>
              <FieldLabel htmlFor="input-code">
                {t("farmerIdentification.form.identificationCode")}
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              </FieldLabel>
              <Input
                {...field}
                id="input-code"
                value={field.value ?? ""}
                disabled={isViewMode}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? "input-code-error" : undefined}
              />
              <FieldError id="input-code-error">{error}</FieldError>
            </Field>
          );
        }}
      />

      <Controller
        control={control}
        name="value_format"
        render={({ field }) => {
          const error = getFieldError("value_format");

          return (
            <Field data-invalid={Boolean(error)}>
              <FieldLabel htmlFor="input-value-format">
                {t("farmerIdentification.form.identificationFormat")}
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              </FieldLabel>
              <Select
                value={field.value ?? ""}
                disabled={isViewMode}
                onValueChange={field.onChange}
              >
                <SelectTrigger
                  id="input-value-format"
                  className="w-full"
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? "input-value-format-error" : undefined}
                >
                  <SelectValue placeholder={t("common.select")} />
                </SelectTrigger>
                <SelectContent>
                  {VALUE_FORMATS.map(valueFormat => (
                    <SelectItem key={valueFormat} value={valueFormat}>
                      {t(VALUE_FORMAT_LABEL_KEYS[valueFormat])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError id="input-value-format-error">{error}</FieldError>
            </Field>
          );
        }}
      />

      <Controller
        control={control}
        name="value_minimum_length"
        render={({ field }) => {
          const error = getFieldError("value_minimum_length");

          return (
            <Field data-invalid={Boolean(error)}>
              <FieldLabel htmlFor="input-value-minimum-length">
                {t("farmerIdentification.form.minimumLengthValidation")}
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              </FieldLabel>
              <Input
                id="input-value-minimum-length"
                type="number"
                min={1}
                name={field.name}
                ref={field.ref}
                value={field.value ?? ""}
                disabled={isViewMode}
                onBlur={field.onBlur}
                onChange={event => field.onChange(event.target.value)}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? "input-value-minimum-length-error" : undefined}
              />
              <FieldError id="input-value-minimum-length-error">{error}</FieldError>
            </Field>
          );
        }}
      />

      <Controller
        control={control}
        name="value_maximum_length"
        render={({ field }) => {
          const error = getFieldError("value_maximum_length");

          return (
            <Field data-invalid={Boolean(error)}>
              <FieldLabel htmlFor="input-value-maximum-length">
                {t("farmerIdentification.form.maximumLengthValidation")}
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              </FieldLabel>
              <Input
                id="input-value-maximum-length"
                type="number"
                min={1}
                name={field.name}
                ref={field.ref}
                value={field.value ?? ""}
                disabled={isViewMode}
                onBlur={field.onBlur}
                onChange={event => field.onChange(event.target.value)}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? "input-value-maximum-length-error" : undefined}
              />
              <FieldError id="input-value-maximum-length-error">{error}</FieldError>
            </Field>
          );
        }}
      />

      <Controller
        control={control}
        name="active"
        render={({ field }) => (
          <div className="flex items-center gap-2">
            <Checkbox
              id="active"
              checked={Boolean(field.value)}
              disabled={isViewMode}
              name={field.name}
              ref={field.ref}
              onBlur={field.onBlur}
              onCheckedChange={checked => field.onChange(checked === true)}
            />
            <Label htmlFor="active" className="font-semibold text-foreground">
              {t("configuration.common.active")}
            </Label>
          </div>
        )}
      />
    </form>
  );
};
